/**
 * VpnCredencialesGeneradasModal (F24.3) — modal post-genera/regenera.
 *
 * Muestra usuario + password en claro UNA SOLA VEZ. Tabs:
 *   - "Mikrotik": snippet RouterOS listo (carga del endpoint snippet)
 *   - "Resumen": user/pass para copiar manual
 */
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, Download, Terminal } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { descargarOvpnConfig, useMikrotikSnippet } from "./api";
import type { OvpnCredencialesGeneradasResponse } from "./schemas";

type Props = {
  cred: OvpnCredencialesGeneradasResponse | null;
  onClose: () => void;
};

export function VpnCredencialesGeneradasModal({ cred, onClose }: Props) {
  const open = cred !== null;
  const [tab, setTab] = useState("mikrotik");
  // Carga el snippet justo cuando hay credencial
  const { data: snippet, isLoading } = useMikrotikSnippet(open ? cred!.credencial.id : null);

  if (!cred) return null;

  function copyAll() {
    if (!snippet?.snippet) return;
    navigator.clipboard.writeText(snippet.snippet);
    toast.success("Snippet copiado");
  }

  function copyText(text: string, label: string) {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  }

  async function downloadOvpn() {
    try {
      await descargarOvpnConfig(
        cred!.credencial.id,
        `byteisp-${cred!.credencial.ovpn_user.toLowerCase()}.ovpn`,
      );
      toast.success(".ovpn descargado");
    } catch (e) {
      toast.error("No se pudo descargar", { description: (e as Error).message });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={16} /> Credencial generada
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            <strong>{cred.credencial.descripcion}</strong> · usuario{" "}
            <code className="font-mono">{cred.credencial.ovpn_user}</code>
          </p>
        </DialogHeader>

        <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-400 mb-2">
          ⚠ {cred.mensaje}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mikrotik">
              <Terminal size={12} className="mr-1" />
              Snippet Mikrotik
            </TabsTrigger>
            <TabsTrigger value="resumen">
              <Copy size={12} className="mr-1" />
              Usuario y password
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mikrotik">
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                Pega TODO este bloque en la terminal del Mikrotik (RouterOS 7+).
                Crea el cliente OVPN automáticamente. Sin importar certificados,
                sin pasos manuales.
              </p>
              <pre className="rounded-md border bg-muted/30 p-3 text-[10px] overflow-x-auto whitespace-pre-wrap break-all max-h-[400px] overflow-y-auto">
                {isLoading ? "Cargando snippet…" : (snippet?.snippet ?? "")}
              </pre>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={downloadOvpn}>
                  <Download size={12} /> .ovpn (PC)
                </Button>
                <Button size="sm" onClick={copyAll} disabled={!snippet?.snippet}>
                  <Copy size={12} /> Copiar snippet
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="resumen">
            <div className="flex flex-col gap-2">
              <CredField
                label="Usuario"
                value={cred.credencial.ovpn_user}
                onCopy={copyText}
              />
              <CredField
                label="Password"
                value={cred.ovpn_pass_plaintext}
                onCopy={copyText}
                mono
              />
              <p className="text-[10px] text-muted-foreground">
                Para configuración manual en otros clientes OpenVPN o si quieres
                copiar el password aparte.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-2">
          <Button size="sm" onClick={onClose}>Listo</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CredField({
  label, value, onCopy, mono,
}: {
  label: string;
  value: string;
  onCopy: (text: string, label: string) => void;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-20">{label}</span>
      <code className={`flex-1 rounded bg-muted/30 px-2 py-1 text-xs ${mono ? "font-mono" : ""} break-all`}>
        {value}
      </code>
      <Button size="icon" variant="ghost" onClick={() => onCopy(value, label)} title="Copiar">
        <Copy size={14} />
      </Button>
    </div>
  );
}
